import { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

export default function SafetyDesk() {
  const [socialReports, setSocialReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workingKey, setWorkingKey] = useState('');

  async function loadReports() {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/platform/social-reports');
      setSocialReports(response.data.socialReports || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load moderation reports');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReports();
  }, []);

  async function assignToMe(id) {
    const key = `assign:${id}`;
    setWorkingKey(key);
    try {
      await api.post(`/admin/platform/social-reports/${id}/assign`);
      await loadReports();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not assign this report');
    } finally {
      setWorkingKey('');
    }
  }

  async function updateStatus(id, status) {
    const key = `status:${id}:${status}`;
    setWorkingKey(key);
    try {
      await api.post(`/admin/platform/social-reports/${id}/status`, { status });
      await loadReports();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not update this report');
    } finally {
      setWorkingKey('');
    }
  }

  async function runModerationEnforcement(id, action) {
    const key = `enforce:${id}:${action}`;
    setWorkingKey(key);
    try {
      await api.post(`/admin/platform/social-reports/${id}/enforce`, { action });
      await loadReports();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not apply this moderation action');
    } finally {
      setWorkingKey('');
    }
  }

  const summary = useMemo(
    () => ({
      total: socialReports.length,
      open: socialReports.filter((item) => item.status === 'open').length,
      inReview: socialReports.filter((item) => item.status === 'in_review').length,
      resolved: socialReports.filter((item) => item.status === 'resolved').length,
    }),
    [socialReports]
  );

  return (
    <Layout>
      <div className="space-y-8">
        <section className="rounded-[2.2rem] border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.22),transparent_36%),linear-gradient(180deg,#111111,#020202)] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.4)] sm:p-8">
          <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#f7d88a]">
            Safety Desk
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
            Moderation and enforcement have their own page now.
          </h1>
          <p className="mt-3 max-w-4xl text-sm leading-7 text-[#cfcfcf]">
            Abuse reports, suspensions, forced blocks, and cleanup actions stay isolated from the customer support queue so reviewers can work faster and with less clutter.
          </p>
        </section>

        <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard title="Total reports" value={summary.total} body="All loaded moderation items" />
          <MetricCard title="Open" value={summary.open} body="Fresh reports still waiting" />
          <MetricCard title="In review" value={summary.inReview} body="Reports under investigation" />
          <MetricCard title="Resolved" value={summary.resolved} body="Closed moderation work" />
        </section>

        {error ? (
          <div className="rounded-[1.8rem] border border-red-500/25 bg-red-950/15 px-5 py-4 text-sm text-red-200">
            {error}
          </div>
        ) : null}

        <section className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-2xl font-black tracking-[-0.03em] text-white">
                Social moderation reports
              </h2>
              <p className="mt-2 text-sm text-[#b8b8b8]">
                Review abuse reports, apply enforcement, and restore access from a dedicated safety desk.
              </p>
            </div>
            <button
              type="button"
              onClick={loadReports}
              className="rounded-full border border-[#f5b221]/28 px-5 py-3 text-sm font-bold text-[#f7d88a] transition hover:bg-[#f5b221]/10"
            >
              Refresh desk
            </button>
          </div>

          {loading ? (
            <div className="mt-6 rounded-[1.6rem] border border-dashed border-[#2b2b2b] bg-black/40 p-5 text-sm text-[#989898]">
              Loading moderation desk...
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {socialReports.length === 0 ? (
                <div className="rounded-[1.6rem] border border-dashed border-[#2b2b2b] bg-black/40 p-5 text-sm text-[#989898]">
                  No moderation reports are waiting right now.
                </div>
              ) : (
                socialReports.map((item) => (
                  <div key={item._id} className="rounded-[1.8rem] border border-[#f5b221]/10 bg-black/60 p-5">
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                      <div>
                        <h3 className="text-lg font-bold capitalize text-white">
                          {String(item.reason || 'report').replaceAll('_', ' ')}
                        </h3>
                        <p className="mt-1 text-sm text-[#9f9f9f]">
                          Reporter:{' '}
                          {`${item.reporterId?.firstName || ''} ${item.reporterId?.lastName || ''}`.trim() ||
                            item.reporterId?.email ||
                            item.reporterId?.shimbirrId ||
                            'Unknown'}
                          {' · '}
                          Target:{' '}
                          {`${item.targetUserId?.firstName || ''} ${item.targetUserId?.lastName || ''}`.trim() ||
                            item.targetUserId?.email ||
                            item.targetUserId?.shimbirrId ||
                            'Unknown'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs font-bold uppercase">
                        <span className="rounded-full bg-[#151515] px-3 py-1 text-[#f7d88a]">
                          {item.status}
                        </span>
                        <span className="rounded-full bg-[#f5b221]/10 px-3 py-1 text-[#f5b221]">
                          {item.reason}
                        </span>
                      </div>
                    </div>

                    {item.details ? (
                      <p className="mt-4 rounded-[1.2rem] bg-[#111111] px-4 py-3 text-sm text-[#c9c9c9]">
                        {item.details}
                      </p>
                    ) : null}

                    <div className="mt-4 grid gap-3 text-sm text-[#b6b6b6] sm:grid-cols-2 xl:grid-cols-4">
                      <InfoField label="Reporter ID" value={item.reporterId?.shimbirrId || item.reporterId?.email || 'Unknown'} />
                      <InfoField label="Target ID" value={item.targetUserId?.shimbirrId || item.targetUserId?.email || 'Unknown'} />
                      <InfoField
                        label="Assigned"
                        value={
                          item.assignedAdminId
                            ? `${item.assignedAdminId.firstName || ''} ${item.assignedAdminId.lastName || ''}`.trim() || item.assignedAdminId.email
                            : 'Nobody yet'
                        }
                      />
                      <InfoField
                        label="Reported"
                        value={item.createdAt ? new Date(item.createdAt).toLocaleString() : 'Unknown'}
                      />
                    </div>

                    {item.targetUserId?.socialModeration?.accessSuspendedAt ? (
                      <div className="mt-4 rounded-[1.2rem] border border-red-500/30 bg-red-950/20 px-4 py-3 text-sm text-red-100">
                        Suspended social access
                        {item.targetUserId?.socialModeration?.accessSuspendedUntil
                          ? ` until ${new Date(item.targetUserId.socialModeration.accessSuspendedUntil).toLocaleString()}`
                          : ''}
                        {item.targetUserId?.socialModeration?.accessSuspensionReason
                          ? ` · ${item.targetUserId.socialModeration.accessSuspensionReason}`
                          : ''}
                      </div>
                    ) : null}

                    {item.lastEnforcementAction ? (
                      <div className="mt-4 rounded-[1.2rem] bg-[#111111] px-4 py-3 text-sm text-[#c9c9c9]">
                        Last enforcement: <span className="font-bold text-white">{item.lastEnforcementAction}</span>
                        {item.enforcedAt ? ` · ${new Date(item.enforcedAt).toLocaleString()}` : ''}
                        {item.clearedMessageCount ? ` · cleared ${item.clearedMessageCount} messages` : ''}
                      </div>
                    ) : null}

                    <div className="mt-4 flex flex-wrap gap-3">
                      <ActionButton
                        label="Assign to me"
                        busy={workingKey === `assign:${item._id}`}
                        onClick={() => assignToMe(item._id)}
                        tone="solid"
                      />
                      <ActionButton
                        label="Move to review"
                        busy={workingKey === `status:${item._id}:in_review`}
                        onClick={() => updateStatus(item._id, 'in_review')}
                      />
                      <ActionButton
                        label="Resolve"
                        busy={workingKey === `status:${item._id}:resolved`}
                        onClick={() => updateStatus(item._id, 'resolved')}
                      />
                      <ActionButton
                        label="Dismiss"
                        busy={workingKey === `status:${item._id}:dismissed`}
                        onClick={() => updateStatus(item._id, 'dismissed')}
                      />
                      <ActionButton
                        label="Suspend social"
                        busy={workingKey === `enforce:${item._id}:suspend_social`}
                        onClick={() => runModerationEnforcement(item._id, 'suspend_social')}
                      />
                      <ActionButton
                        label="Restore access"
                        busy={workingKey === `enforce:${item._id}:restore_social`}
                        onClick={() => runModerationEnforcement(item._id, 'restore_social')}
                      />
                      <ActionButton
                        label="Force block pair"
                        busy={workingKey === `enforce:${item._id}:force_block`}
                        onClick={() => runModerationEnforcement(item._id, 'force_block')}
                      />
                      <ActionButton
                        label="Clear recent messages"
                        busy={workingKey === `enforce:${item._id}:clear_recent_messages`}
                        onClick={() => runModerationEnforcement(item._id, 'clear_recent_messages')}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function MetricCard({ title, value, body }) {
  return (
    <div className="rounded-[1.8rem] border border-[#f5b221]/12 bg-[linear-gradient(180deg,#111111,#040404)] p-5">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8f8f8f]">{title}</p>
      <p className="mt-3 text-4xl font-black tracking-[-0.04em] text-[#f5b221]">{value}</p>
      <p className="mt-2 text-sm text-[#b4b4b4]">{body}</p>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7f7f7f]">{label}</p>
      <p className="mt-1 text-white">{value}</p>
    </div>
  );
}

function ActionButton({ label, busy, onClick, tone = 'outline' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={[
        'rounded-full px-4 py-2 text-sm font-bold transition disabled:opacity-60',
        tone === 'solid'
          ? 'bg-[#f5b221] text-black hover:bg-[#f7d88a]'
          : 'border border-[#333333] text-white hover:bg-[#111111]',
      ].join(' ')}
    >
      {busy ? 'Working...' : label}
    </button>
  );
}
